import { type KeyboardEvent, useRef, useState } from "react";

/**
 * The ARIA composite-widget pattern: a group of controls that costs a single Tab
 * stop, with the arrow keys moving between them.
 *
 * Worth reaching for wherever a group repeats — a toolbar on every card in a
 * list, or a pair of actions on every line of a transcript — because there the
 * cost is not the group but the number of them between the reader and whatever
 * they were actually heading for.
 *
 * Horizontal only, by design. Up and down belong to the page: to the reader
 * scrolling, and to a screen reader's own browse-mode navigation.
 *
 * The caller applies `tabIndexFor(i)` to each control in DOM order, so the
 * indices here and the ones in the markup are the same indices. Disabled
 * controls are deliberately not skipped for that reason; no current caller has
 * any, and quietly renumbering would be worse than the gap.
 */
export function useRovingTabindex(count: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  // Clamped: the controls come and go with permissions and overflow, and an
  // index left pointing past the end would leave the group with no tab stop
  // at all — unreachable rather than merely awkward.
  const activeIndex = count > 0 ? Math.min(active, count - 1) : 0;

  const focusAt = (index: number) => {
    const items = Array.from(
      ref.current?.querySelectorAll<HTMLElement>("button, a[href]") ?? [],
    );
    if (items.length === 0) return;
    const next = (index + items.length) % items.length;
    setActive(next);
    items[next]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const target =
      event.key === "ArrowRight"
        ? activeIndex + 1
        : event.key === "ArrowLeft"
          ? activeIndex - 1
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? -1
              : null;
    if (target === null) return;
    event.preventDefault();
    focusAt(target);
  };

  return {
    ref,
    onKeyDown,
    tabIndexFor: (index: number) => (index === activeIndex ? 0 : -1),
  };
}
